import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  getSteps,
  restoreUi,
  snapshotUi,
  SEEN_STORAGE_KEY,
  RELEASE_ARTICLE_URL,
  RELEASE_TUTORIAL_PAGE,
} from "./whatsNewSteps";
import {
  getLocalStorage,
  saveTutorialStatusToLocal,
} from "../../helpers/common";
import { saveTutorialStatus } from "../../services/commonServices";
import { USER_INFO_KEY } from "../../constants/common";
import "./WhatsNew.scss";

const PADDING = 8;
const TOOLTIP_W = 360;
const GAP = 16;

/**
 * A spotlight over the controls that moved in the release. Length is not
 * fixed - steps whose target is absent drop themselves, so the same
 * definition gives a desktop agent and a phone agent each the steps that
 * apply to them.
 *
 * The dark ground is a single enormous box-shadow on the cutout rather than
 * four masking divs or an SVG mask: it is one compositor-friendly element, and
 * because it is pointer-events:none the page underneath still scrolls, which
 * matters when a target sits inside the feed's own scroll container.
 */
// target may be a selector, a function returning an element, or null.
const isShown = (el) => {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return el.offsetParent !== null && r.width > 0 && r.height > 0;
};

// Resolve to the first VISIBLE match. The rail and the mobile drawer render
// the same controls, and the hidden copy usually comes first in the DOM - a
// plain querySelector returns it and the ring collapses to nothing.
const resolveTarget = (t) => {
  if (t === null || t === undefined) return null;
  try {
    if (typeof t === "function") return t();
    return [...document.querySelectorAll(t)].find(isShown) || null;
  } catch {
    return null;
  }
};

// A worked example of what a score is made of. Illustrative, not live data -
// the point is that a percentage now comes with its reasons attached.
const MatchVisual = () => (
  <div className="whats-new__match" aria-hidden="true">
    <div className="whats-new__match-score">
      <span className="whats-new__match-pct">94%</span>
      <span className="whats-new__match-label">Match</span>
    </div>
    <ul className="whats-new__match-reasons">
      <li>Inside their search area</li>
      <li>Budget covers your asking price</li>
      <li>Beds and baths both fit</li>
      <li>Has 2 of 3 must-haves</li>
    </ul>
  </div>
);

// Click-through of cropped screenshots. Used where an agent cannot be shown
// the thing in their own account - a score needs matches to exist, a
// suggestion needs a near miss - so a spotlight would ring an empty screen.
const Gallery = ({ items }) => {
  const [i, setI] = useState(0);
  const item = items[i];
  return (
    <div className="whats-new__gallery">
      <img src={item.src} alt="" className="whats-new__gallery-img" />
      <div className="whats-new__gallery-foot">
        <p className="whats-new__gallery-cap">{item.caption}</p>
        {items.length > 1 && (
          <div className="whats-new__dots">
            {items.map((_, n) => (
              <button
                key={n}
                type="button"
                aria-label={`Image ${n + 1} of ${items.length}`}
                aria-current={n === i}
                className={`whats-new__dot${n === i ? " is-on" : ""}`}
                onClick={() => setI(n)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const WhatsNew = ({ open, onClose }) => {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [ready, setReady] = useState(false);
  // Steps whose target never turned up once the step had actually run.
  const [dropped, setDropped] = useState(() => new Set());
  const rafRef = useRef(null);
  const cardRef = useRef(null);
  // What the feed looked like before the tour touched anything.
  const snapshotRef = useRef(null);

  // A step whose element is not VISIBLE would spotlight nothing, so it is
  // dropped up front. Existence is not enough: the mobile bar is rendered on
  // desktop too and merely hidden by d-md-none, so a querySelector check alone
  // kept a step that then ringed a zero-size box. A step with target null is
  // deliberate - it has no single control to point at - and is kept.
  const allSteps = useMemo(() => {
    if (!open) return [];
    // Surface is decided once, when the tour opens.
    return getSteps().filter((s) => {
      if (s.available) return s.available();
      if (s.target === null) return true;
      return isShown(resolveTarget(s.target));
    });
  }, [open]);

  // The filter above cannot judge a step whose own onEnter is what creates the
  // target, so those are admitted on an `available` surface check and can still
  // turn out to describe a control this agent does not have - an agent with no
  // properties has no selector to ring. Removing the step from the list is what
  // advances past it: the list shrinks under the current index, so the next
  // step slides into place and "2 of 4" stays honest.
  const steps = useMemo(
    () => allSteps.filter((s) => !dropped.has(s.id)),
    [allSteps, dropped],
  );

  // Snapshotting is a side effect (reads live DOM state to restore later), so
  // it belongs in an effect rather than in the steps memo above - a memo body
  // must stay pure, and React may invoke it more than once per commit.
  // Declared before the onEnter effect below so it always captures state
  // before any step has a chance to mutate it.
  useEffect(() => {
    if (!open) return;
    snapshotRef.current = snapshotUi();
    setDropped((prev) => (prev.size ? new Set() : prev));
  }, [open]);

  // Two stores, because neither is sufficient alone: the localStorage key
  // answers on the first render with no round trip, and the Tutorials row ties
  // "seen" to the account. The row is what survives a cleared cache - clearing
  // it logs the agent out too, and login re-reads the row back into
  // tutorialDisplayedPages. Both are keyed to this release, so the next one
  // still announces itself.
  const markSeen = useCallback(() => {
    try {
      localStorage.setItem(SEEN_STORAGE_KEY, "1");
    } catch {
      // Private browsing or a full quota - the account-level row still holds.
    }
    saveTutorialStatusToLocal(RELEASE_TUTORIAL_PAGE);
    // Signed out there is no row to write, and the tour is agent-only anyway.
    if (!getLocalStorage(USER_INFO_KEY)?.userInfo?.userId) return;
    // A failed write only costs the agent one extra showing, so nothing here
    // is allowed to reach the render that called it.
    try {
      Promise.resolve(saveTutorialStatus(RELEASE_TUTORIAL_PAGE, 1)).catch(
        () => {},
      );
    } catch {
      /* empty */
    }
  }, []);

  // Marked on open, not on Skip/Done. The overlay is pointer-events:none by
  // design, so an agent can click straight through into the feed and navigate
  // away mid-tour; marking only on the two deliberate exits let the tour come
  // back on their next visit, which is what it is meant not to do.
  useEffect(() => {
    if (!open) return;
    markSeen();
  }, [open, markSeen]);

  const step = steps[index];

  const measure = useCallback(() => {
    if (!step) return;
    if (step.target === null) {
      setRect(null);
      return;
    }
    const el = resolveTarget(step.target);
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Tagged with the step it belongs to. Without this the previous step's rect
    // survives into the next one, and the ring sits on the control the last
    // card described while the new card talks about something else.
    const next = {
      id: step.id,
      x: r.x - PADDING,
      y: r.y - PADDING,
      width: r.width + PADDING * 2,
      height: r.height + PADDING * 2,
    };
    // Ignore sub-pixel churn. Smooth scrolling fires a stream of tiny changes,
    // and re-rendering on each one slides the card - and its Next button - out
    // from under the pointer mid-click, which reads as the tour freezing.
    setRect((prev) => {
      if (
        prev &&
        prev.id === next.id &&
        Math.abs(prev.x - next.x) < 2 &&
        Math.abs(prev.y - next.y) < 2 &&
        Math.abs(prev.width - next.width) < 2 &&
        Math.abs(prev.height - next.height) < 2
      ) {
        return prev;
      }
      return next;
    });
  }, [step]);

  // Bring the target into view before measuring, or the ring lands off-screen
  // for anything below the fold.
  useEffect(() => {
    if (!open || !step) return undefined;
    setReady(false);
    if (step.target === null) {
      setRect(null);
      setReady(true);
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    (async () => {
      // Put the UI into the state this step describes before measuring it.
      if (step.onEnter) {
        try {
          await step.onEnter();
        } catch {
          // A step that cannot set itself up still shows its card.
        }
      }
      if (cancelled) return;
      resolveTarget(step.target)?.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
      // A panel that has just opened is still animating and may scroll its own
      // content, so a single measurement lands the ring where the target used
      // to be. Poll until the rect stops moving, then stop.
      let last = null;
      let stable = 0;
      let tries = 0;
      // A target that never stops moving - a sticky bar reacting to scroll,
      // rubber-banding on iOS - would otherwise poll for the life of the step
      // and never set ready. Cap it; the ring still lands close enough.
      const MAX_TRIES = 25;
      // A target that is absent rather than merely moving is a different
      // failure: the step describes a control this agent does not have. Give up
      // on it well before the settle budget runs out and drop the step, instead
      // of holding the tour on a card with nothing to point at.
      const MAX_MISSES = 8;
      let misses = 0;
      const settle = () => {
        if (cancelled) return;
        const el = resolveTarget(step.target);
        if (!el) {
          misses += 1;
          if (misses >= MAX_MISSES) {
            setDropped((prev) => new Set(prev).add(step.id));
            return;
          }
          timer = setTimeout(settle, 90);
          return;
        }
        const r = el.getBoundingClientRect();
        const key = `${Math.round(r.x)},${Math.round(r.y)},${Math.round(r.width)},${Math.round(r.height)}`;
        stable = key === last ? stable + 1 : 0;
        last = key;
        measure();
        tries += 1;
        if (stable >= 2 || tries >= MAX_TRIES) {
          setReady(true);
          return;
        }
        timer = setTimeout(settle, 90);
      };
      timer = setTimeout(settle, 160);
    })();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, step, measure]);

  useEffect(() => {
    if (!open) return undefined;
    const onChange = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    };
    window.addEventListener("resize", onChange);
    window.addEventListener("scroll", onChange, true);
    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("scroll", onChange, true);
      cancelAnimationFrame(rafRef.current);
    };
  }, [open, measure]);

  const finish = useCallback(() => {
    markSeen();
    setIndex(0);
    setDropped(new Set());
    onClose?.();
    // Leave the feed as it was found. Steps open panels to show what is in
    // them, and Skip can land mid-way through that.
    restoreUi(snapshotRef.current);
  }, [onClose, markSeen]);

  const next = useCallback(() => {
    if (index < steps.length - 1) setIndex((i) => i + 1);
    else finish();
  }, [index, steps.length, finish]);

  // Dropping a step is how the tour advances past it, but there is nothing to
  // advance to if it was the last one - end the tour rather than sit on an
  // index past the end, which renders nothing at all.
  useEffect(() => {
    if (!open || !steps.length) return;
    if (index >= steps.length) finish();
  }, [open, index, steps.length, finish]);

  // Put focus in the card so a keyboard agent starts inside the tour rather
  // than at the top of the page. Placed, not trapped: the overlay is
  // pointer-events:none by design so the feed underneath still scrolls, which
  // is why it does not claim aria-modal. preventScroll keeps this from
  // fighting the scrollIntoView that positions the ring.
  useEffect(() => {
    if (!open || !ready) return;
    cardRef.current?.focus({ preventScroll: true });
  }, [open, ready, step]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") finish();
      if (e.key === "ArrowRight") next();
      // Enter is deliberately not treated as "next" here: Skip, Next, the
      // gallery dots and the release link are all real buttons/links inside
      // the card, so a focused control already advances/acts on its own
      // Enter. Handling it again at the document level double-fired - Skip
      // both closed the tour and advanced it, a dot both changed image and
      // advanced.
      if (e.key === "ArrowLeft" && index > 0) setIndex((i) => i - 1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, finish, next, index]);

  if (!open || !step) return null;
  // Only ever draw a ring that was measured for THIS step. A rect left over
  // from the previous one would spotlight the wrong control; holding the card
  // back for the ~250ms until this step measures - or until it gives up and
  // drops itself - is the lesser of the two.
  if (step.target !== null && rect?.id !== step.id) return null;

  const centered = step.target === null;
  // A bottom sheet owns the lower part of the screen and paints above anything
  // we can set, so the card moves to the top instead of overlapping it.
  const sheetOpen =
    typeof document !== "undefined" &&
    !!document.querySelector(".offcanvas.feed-drawer.show");

  // Prefer the requested side, but never let the card leave the viewport.
  let top = 0;
  let left = 0;
  // Set instead of `top` for a target sitting at the bottom of the screen.
  // Anchoring by the card's bottom edge is the only way to clear the target
  // without knowing how tall the card turns out to be - a top-based guess that
  // runs short covers the very control the step is pointing at, which is what
  // the mobile bottom nav did.
  let bottom = null;
  if (centered) {
    // Nothing to position against.
  } else if (step.placement === "top") {
    bottom = Math.max(GAP, window.innerHeight - rect.y + GAP);
    left = Math.max(
      GAP,
      Math.min(rect.x, window.innerWidth - TOOLTIP_W - GAP),
    );
  } else {
    top = rect.y;
    left =
      step.placement === "left"
        ? rect.x - TOOLTIP_W - GAP
        : step.placement === "below"
          ? rect.x
          : rect.x + rect.width + GAP;

    if (step.placement === "below") {
      top = rect.y + rect.height + GAP;
    }
    if (left + TOOLTIP_W > window.innerWidth - GAP) {
      left = rect.x - TOOLTIP_W - GAP;
    }
    if (left < GAP) {
      left = GAP;
      top = rect.y + rect.height + GAP;
    }
    if (top + 210 > window.innerHeight) {
      top = Math.max(GAP, window.innerHeight - 210 - GAP);
    }
  }

  return createPortal(
    <div
      className={`whats-new${sheetOpen ? " whats-new--sheet" : ""}`}
      role="dialog"
      aria-label="What's new"
    >
      {centered ? (
        <div className="whats-new__scrim" />
      ) : (
        <div
          className={`whats-new__cutout${ready ? " whats-new__cutout--animate" : ""}`}
          style={{
            transform: `translate(${rect.x}px, ${rect.y}px)`,
            width: rect.width,
            height: rect.height,
          }}
        />
      )}

      <div
        ref={cardRef}
        tabIndex={-1}
        className={`whats-new__card${centered ? " whats-new__card--centered" : ""}`}
        style={
          centered
            ? undefined
            : bottom !== null
              ? { bottom, left, width: TOOLTIP_W }
              : { top, left, width: TOOLTIP_W }
        }
      >
        <p className="whats-new__eyebrow">What&rsquo;s new</p>
        <h3 className="whats-new__title">
          {step.title}
          {step.badge && (
            <span className="whats-new__badge">{step.badge}</span>
          )}
        </h3>
        <p className="whats-new__desc">{step.description}</p>
        {step.gallery && <Gallery items={step.gallery} />}
        {step.visual === "match" && <MatchVisual />}

        <div className="whats-new__foot">
          <button type="button" className="whats-new__skip" onClick={finish}>
            Skip
          </button>
          <span className="whats-new__count">
            {index + 1} of {steps.length}
          </span>
          <button type="button" className="whats-new__next" onClick={next}>
            {index === steps.length - 1 ? "Done" : "Next"}
          </button>
        </div>

        <a
          className="whats-new__link"
          href={RELEASE_ARTICLE_URL}
          target="_blank"
          rel="noreferrer"
        >
          See everything that changed
        </a>
      </div>
    </div>,
    document.body,
  );
};

export default WhatsNew;
